import { useEffect, useRef } from 'react';
import { useCallState } from '../context/CallStateContext';

/**
 * New-task alerting: desktop notification, tab title, sound, taskbar badge.
 *
 * Mounted ONCE inside CallStateProvider (App.js) rather than on a page, because
 * a page-mounted alert only fires while that page is open. This lived in
 * TasksPage, so a receptionist sitting on the Dashboard with the window
 * minimized was never told a task had arrived. The alert rides the
 * call-monitor socket (see `task_alert` in CallStateContext), which is the one
 * connection open on every page.
 *
 * Everything is gated on the window being away — hidden tab, minimized, or
 * another app in front. Someone looking at the screen needs no interrupting;
 * the Tasks page highlights the new row on its own.
 *
 * "Unseen" clears on focus, not on navigation, so the count survives moving
 * between pages and only resets when the user actually comes back.
 */
const IDLE_TITLE = 'DeskBot AI Phone Assistant';

export const TaskAlerts = () => {
  const { subscribeTaskAlerts, activeCall } = useCallState();
  const unseenRef = useRef(0);
  const focusedRef = useRef(true);
  const audioRef = useRef(null);
  const alertedIdsRef = useRef(new Set());
  const closeTimerRef = useRef(null);
  const activeCallRef = useRef(activeCall);
  activeCallRef.current = activeCall;

  // One interval owns document.title for both the call indicator and the unseen
  // count. They used to run in separate components and overwrite each other, so
  // a task arriving during a call blinked invisibly.
  useEffect(() => {
    const paint = () => {
      const on = Math.floor(Date.now() / 1000) % 2 === 0;
      if (activeCallRef.current) {
        document.title = on ? '\u{1F534} DeskBot' : '\u{1F7E2} DeskBot';
      } else if (unseenRef.current > 0) {
        const n = unseenRef.current;
        document.title = on
          ? `\u{1F534} ${n > 1 ? `${n} neue Aufgaben` : 'Neue Aufgabe'}!`
          : 'DeskBot';
      } else {
        document.title = IDLE_TITLE;
      }
    };
    paint();
    const id = setInterval(paint, 500);
    return () => {
      clearInterval(id);
      document.title = IDLE_TITLE;
    };
  }, []);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    audioRef.current = new Audio('/notification.wav');
    audioRef.current.volume = 0.5;

    const onFocus = () => {
      focusedRef.current = true;
      unseenRef.current = 0;
      navigator.clearAppBadge?.().catch(() => {});
    };
    const onBlur = () => { focusedRef.current = false; };
    const onVisibility = () => { if (!document.hidden) onFocus(); };

    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => subscribeTaskAlerts((data) => {
    // The socket reconnects on its own and the backend does not deduplicate, so
    // guard against alerting twice for the same task.
    if (!data?.id || alertedIdsRef.current.has(data.id)) return;
    alertedIdsRef.current.add(data.id);

    if (!document.hidden && focusedRef.current) return;

    const unseen = ++unseenRef.current;
    navigator.setAppBadge?.(unseen).catch(() => {});

    if ('Notification' in window && Notification.permission === 'granted') {
      const names = data.caller_name || [];
      const caller = (Array.isArray(names) ? names.filter(n => n !== '__pvs__')[0] : names) || 'Unbekannt';
      const note = new Notification(unseen > 1 ? `${unseen} neue Aufgaben` : 'Neue Aufgabe', {
        body: `${caller} – ${data.description || 'Neue Aufgabe eingegangen'}`,
        icon: '/Deskbot-newlogo-favicon.png',
        tag: 'deskbot-new-task',
        renotify: true,
        requireInteraction: true
      });
      note.onclick = () => { window.focus(); note.close(); };
      // requireInteraction keeps it up until closed, so close it after 10 s.
      // Notifications share one tag and replace each other, so restart the
      // timer or an older task's timer would close the newer notification.
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(() => note.close(), 10000);
    }

    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }), [subscribeTaskAlerts]);

  return null;
};

export default TaskAlerts;
